/**
 * Validation Utility Functions
 * Provides common validation logic for forms and data
 */

export class ValidationUtils {
  /**
   * Email validation regex (RFC 5322 simplified)
   */
  private static readonly EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * Password strength regex (min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char)
   */
  private static readonly PASSWORD_STRENGTH_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  /**
   * Validate email address
   */
  static isValidEmail(email: string): boolean {
    if (!email) {
      return false;
    }
    return this.EMAIL_REGEX.test(email);
  }

  /**
   * Validate password strength
   */
  static isStrongPassword(password: string): boolean {
    if (!password) {
      return false;
    }
    return this.PASSWORD_STRENGTH_REGEX.test(password);
  }

  /**
   * Check password requirements individually
   */
  static getPasswordRequirements(password: string): PasswordRequirements {
    return {
      hasMinLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&]/.test(password),
    };
  }

  /**
   * Validate that passwords match
   */
  static passwordsMatch(password: string, confirmPassword: string): boolean {
    return password === confirmPassword && password.length > 0;
  }

  /**
   * Validate required field
   */
  static isRequired(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return true;
  }

  /**
   * Validate minimum length
   */
  static hasMinLength(value: string, minLength: number): boolean {
    return !!value && value.length >= minLength;
  }

  /**
   * Validate maximum length
   */
  static hasMaxLength(value: string, maxLength: number): boolean {
    return !value || value.length <= maxLength;
  }

  /**
   * Validate OTP (6 digits)
   */
  static isValidOTP(otp: string): boolean {
    return /^\d{6}$/.test(otp);
  }

  /**
   * Sanitize HTML to prevent XSS
   */
  static sanitizeHTML(html: string): string {
    const div = document.createElement('div');
    div.textContent = html;
    return div.innerHTML;
  }
}

/**
 * Password Requirements Interface
 */
export interface PasswordRequirements {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}
